import isString from 'lodash/isString';
import last from 'lodash/last';
import split from 'lodash/split';
import toLower from 'lodash/toLower';
import map from 'lodash/map';
import replace from 'lodash/replace';
import get from 'lodash/get';
import defaults from 'lodash/defaults';
import includes from 'lodash/includes';
import isNumber from 'lodash/isNumber';
import { IMAGE_EXTENSIONS } from './files.const';
/**
 * Determines if the provided path contains an image file
 * by checking against image extension list
 * @param source {string | number | null | undefined}
 * @returns boolean
 */
export function isImageExtension(source) {
    if (!isString(source) || !source) {
        return false;
    }
    const extension = source.includes('.') ? last(split(source, '.')) : source;
    return IMAGE_EXTENSIONS.includes(toLower(extension));
}
/**
 * Removes the URI header 'data:image/png;base64,'
 * @param uri {string}
 * @returns {string}
 */
export function removeBase64Header(uri) {
    return replace(uri, /^data:.+;base64,/g, '');
}
/**
 * Removes the headers in an URI array'
 * @param uris {string[]}
 * @returns {string[]}
 */
export function removeBase64HeaderArray(uris) {
    return map(uris, removeBase64Header);
}
/**
 * removes Name from Base64 URI'
 * @param image {string}
 * @returns {string}
 */
export function base64WithoutName(image) {
    const index = image.indexOf('{{');
    if (isString(image) && index > -1) {
        return image.substring(0, index);
    }
    return image;
}
/**
 * Checks if the image path exists
 * @param src {string}
 * @returns {Promise<boolean>}
 */
export function isImage(src) {
    return new Promise(resolve => {
        const image = new Image();
        image.onload = () => {
            return resolve(true);
        };
        image.onerror = () => {
            return resolve(false);
        };
        image.src = src;
    });
}
/**
 * Returns the image name from an URI and an URL
 * @param image {string}
 * @returns {string}
 */
export function getImageName(image) {
    if (isString(image)) {
        if (image.indexOf('{{') > -1) {
            return get(image.match(/{{(.*?)}}+/), 0, '').replace(/[{}]/g, '');
        }
        if (image.startsWith('http')) {
            return image.substring(image.lastIndexOf('/') + 1);
        }
    }
    return image;
}
/**
 * Returns the Base64 from a File
 * @param file {File}
 * @param options {Base64Options}
 * @returns {Promise<string>}
 */
export function getBase64(file, options = {}) {
    const base64Options = defaults(options, {
        hasFilename: true,
        isHeaderRemoved: false,
        types: IMAGE_EXTENSIONS,
        maxSize: 9999999999,
    });
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
        if (!file) {
            reject(Error('No file provided'));
        }
        const type = file.type.replace('image/', '').replace('application/', '');
        if (!includes(base64Options.types, type)) {
            reject(Error(`The type of the file ${file.name} is not valid [${base64Options.types.join(', ')}].`));
        }
        if (isNumber(base64Options.maxSize)) {
            const fileSizeInMB = file.size / 1024 / 1024;
            if (fileSizeInMB > base64Options.maxSize) {
                reject(Error(`The size of the file ${file.name} is bigger than ${base64Options.maxSize}MB`));
            }
        }
        reader.onload = event => {
            const result = get(event, 'target.result');
            let b64 = base64Options.isHeaderRemoved ? removeBase64Header(result) : result;
            if (base64Options.hasFilename) {
                b64 += `{{${file.name}}}`;
            }
            resolve(b64);
        };
        reader.readAsDataURL(file);
    });
}
